// useReviewQueueCounts — lightweight pending-review tally for the
// always-mounted shell chrome (PRD #156).
//
// The sidebar "Review queue" badge and the agent-panel peek only need a
// number, not the full review-queue model. While the Railway backend/API
// service is offline, keep the always-mounted chrome on a zero-count local
// fallback so navigation never waits on a failing endpoint.
//
// The full `useReviewQueue` hook remains the source of truth for the
// review-queue page and the agent review surfaces.

import type {
    FrontendReviewCounts,
    ReviewQueueCountsResponse,
    AgentFrontend,
} from '@septima/contract';

export interface UseReviewQueueCountsResult {
    /** Number of review *items* for the frontend (post-call drafts collapse
     *  to one per call). Drives the sidebar badge. */
    itemCount: number;
    /** Number of individual approve/reject decisions those items carry. */
    decisionCount: number;
    /** True while the first counts fetch is in flight. */
    isLoading: boolean;
    /** True if the counts query errored — callers treat counts as zero. */
    isError: boolean;
}

const ZERO: FrontendReviewCounts = { itemCount: 0, decisionCount: 0 };

/** Staff frontends the counts endpoint reports. `global` has no badge. */
function staffKeyOf(frontend?: AgentFrontend): keyof ReviewQueueCountsResponse | null {
    if (frontend === 'desk' || frontend === 'ops' || frontend === 'comply' || frontend === 'view') {
        return frontend;
    }
    return null;
}

/**
 * Project the counts payload down to a single frontend's tally. Pure, so
 * the empty (no data yet / errored) and missing-frontend fallbacks are
 * unit-testable without a DOM. Returns zeros when `data` is absent (loading
 * or error), when no frontend is given, or when the frontend is `global`.
 */
export function selectFrontendCounts(
    data: ReviewQueueCountsResponse | undefined,
    frontend?: AgentFrontend,
): FrontendReviewCounts {
    const key = staffKeyOf(frontend);
    if (!data || !key) return ZERO;
    return data[key];
}

export function useReviewQueueCounts(frontend?: AgentFrontend): UseReviewQueueCountsResult {
    const counts = selectFrontendCounts(undefined, frontend);
    return {
        itemCount: counts.itemCount,
        decisionCount: counts.decisionCount,
        isLoading: false,
        isError: false,
    };
}
